import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  Search, 
  ShieldCheck, 
  AlertTriangle, 
  CheckCircle2, 
  Lock, 
  Building2, 
  Calendar, 
  Layers, 
  ArrowRight, 
  AlertCircle,
  HelpCircle,
  Sparkles,
  Phone,
  Mail,
  User,
  AtSign
} from 'lucide-react';
import { checkIdentityExposure } from '../api/identity';
import { IdentityCheckResult } from '../types';

export const CheckIdentity: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';

  const [query, setQuery] = useState(initialQuery);
  const [queryType, setQueryType] = useState<'email' | 'phone' | 'name' | 'username'>('email');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<IdentityCheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    try {
      const res = await checkIdentityExposure(query.trim(), queryType);
      setResult(res);
    } catch (err: any) {
      setError(err.message || 'Error conducting identity exposure check.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialQuery) {
      handleSearch();
    }
  }, [initialQuery]);

  const handlePreset = (sampleQuery: string, type: 'email' | 'phone' | 'name' | 'username') => {
    setQuery(sampleQuery);
    setQueryType(type);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-950 text-emerald-300 text-xs font-semibold border border-emerald-800">
          <Search className="w-3.5 h-3.5" />
          <span>Identity Exposure Checker</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white">
          Has your personal information been exposed?
        </h1>
        <p className="text-slate-400 text-sm max-w-xl mx-auto">
          Search across millions of verified Australian and global data breach records to detect compromised accounts and exposed credentials.
        </p>
      </div>

      {/* Search Card */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl space-y-6">
        {/* Type tabs */}
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-800 pb-4">
          <button
            type="button"
            onClick={() => setQueryType('email')}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              queryType === 'email' ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-300 hover:text-white'
            }`}
          >
            <Mail className="w-3.5 h-3.5" />
            Email Address
          </button>
          <button
            type="button"
            onClick={() => setQueryType('phone')}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              queryType === 'phone' ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-300 hover:text-white'
            }`}
          >
            <Phone className="w-3.5 h-3.5" />
            Phone Number (AU)
          </button>
          <button
            type="button"
            onClick={() => setQueryType('name')}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              queryType === 'name' ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-300 hover:text-white'
            }`}
          >
            <User className="w-3.5 h-3.5" />
            Full Legal Name
          </button>
          <button
            type="button"
            onClick={() => setQueryType('username')}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              queryType === 'username' ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-300 hover:text-white'
            }`}
          >
            <AtSign className="w-3.5 h-3.5" />
            Username
          </button>
        </div>

        {/* Input Form */}
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <input
                type={queryType === 'email' ? 'email' : 'text'}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={
                  queryType === 'email' ? 'e.g. yourname@example.com.au' :
                  queryType === 'phone' ? 'e.g. 0412 345 678 or +614...' :
                  queryType === 'name' ? 'e.g. Alex Mitchell' : 'e.g. alexmitchell_au'
                }
                required
                className="w-full px-4 py-3.5 rounded-xl bg-slate-950 border border-slate-700 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-emerald-500 shadow-inner"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="px-6 py-3.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-xl font-semibold text-sm shadow-lg shadow-emerald-950/40 transition-all flex items-center justify-center gap-2 flex-shrink-0"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Searching Breaches...</span>
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  <span>Check Exposure</span>
                </>
              )}
            </button>
          </div>

          {/* Quick preset examples */}
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
            <span className="text-slate-500 font-medium">Try test samples:</span>
            <button
              type="button"
              onClick={() => handlePreset('alex.mitchell@sydney-design.com.au', 'email')}
              className="px-2.5 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 font-mono text-[11px]"
            >
              alex.mitchell@sydney-design.com.au (breach)
            </button>
            <button
              type="button"
              onClick={() => handlePreset('safe.citizen@privacy-clean.au', 'email')}
              className="px-2.5 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 font-mono text-[11px]"
            >
              safe.citizen@privacy-clean.au (clean)
            </button>
            <button
              type="button"
              onClick={() => handlePreset('+61 412 890 341', 'phone')}
              className="px-2.5 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 font-mono text-[11px]"
            >
              +61 412 890 341 (mobile)
            </button>
          </div>
        </form>

        {/* Security Promise Note */}
        <div className="p-4 rounded-xl bg-slate-950/70 border border-slate-800/80 flex items-start gap-3">
          <Lock className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-slate-300 space-y-1">
            <p className="font-semibold text-slate-200">
              We never need your password to check whether your email or other information has appeared in a known breach.
            </p>
            <p className="text-slate-400">
              Your search query is encrypted in transit and never stored or sold. We match against historical breach indexes using cryptographic k-anonymity hashing.
            </p>
          </div>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="p-4 rounded-xl bg-rose-950/80 border border-rose-800 text-rose-200 text-sm flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* RESULTS DISPLAY */}
      {result && (
        <div className="space-y-8 animate-fade-in">
          {/* Status Banner */}
          <div className={`p-6 rounded-2xl border shadow-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
            result.exposureStatus === 'none'
              ? 'bg-emerald-950/40 border-emerald-800/60 text-emerald-200'
              : result.exposureStatus === 'potential'
                ? 'bg-amber-950/40 border-amber-800/60 text-amber-200'
                : 'bg-rose-950/40 border-rose-800/60 text-rose-200'
          }`}>
            <div className="flex items-center gap-3">
              {result.exposureStatus === 'none' ? (
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
                  <CheckCircle2 className="w-7 h-7 text-emerald-400" />
                </div>
              ) : (
                <div className="w-12 h-12 rounded-xl bg-rose-500/20 border border-rose-500/30 flex items-center justify-center">
                  <AlertTriangle className="w-7 h-7 text-rose-400" />
                </div>
              )}
              <div>
                <span className="text-xs uppercase font-bold tracking-wider opacity-80">Identity Exposure Result</span>
                <h3 className="text-xl font-bold text-white">
                  {result.exposureStatus === 'none' && '🟢 No known exposure detected'}
                  {result.exposureStatus === 'potential' && `🟠 Potential exposure detected (${result.breachCount} breach found)`}
                  {result.exposureStatus === 'confirmed' && `🔴 Your information has been found in ${result.breachCount} known breaches`}
                </h3>
                <p className="text-xs opacity-90 mt-0.5">
                  Search query: <span className="font-mono font-bold text-white">{result.query}</span> ({result.type})
                </p>
              </div>
            </div>

            <Link
              to="/register"
              className="px-5 py-2.5 rounded-xl bg-white text-slate-950 hover:bg-slate-100 font-bold text-xs flex items-center gap-1.5 transition-colors flex-shrink-0 shadow-md"
            >
              <span>Start 24/7 Protection</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Exposed Data Classes */}
          {result.exposedDataClasses.length > 0 && (
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-3">
              <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                <Layers className="w-4 h-4 text-emerald-400" />
                <span>Information Potentially Exposed Across Breaches</span>
              </h4>
              <div className="flex flex-wrap gap-2 pt-1">
                {result.exposedDataClasses.map((item, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 rounded-lg text-xs font-medium bg-rose-950/50 border border-rose-800/60 text-rose-300"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Breaches List */}
          {result.breaches.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-base font-bold text-white">
                Detailed Breach Incidents ({result.breaches.length})
              </h4>
              <div className="grid grid-cols-1 gap-4">
                {result.breaches.map((breach) => (
                  <div
                    key={breach.id}
                    className="p-6 rounded-2xl bg-slate-900 border border-slate-800 space-y-4 shadow-lg"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-800 pb-3">
                      <div>
                        <h5 className="text-base font-bold text-white">{breach.name}</h5>
                        <p className="text-xs text-slate-400 font-mono">{breach.domain} • {breach.organisation}</p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <Calendar className="w-3.5 h-3.5 text-slate-500" />
                        <span>Breach Date: {breach.breachDate}</span>
                      </div>
                    </div>

                    <p className="text-xs text-slate-300 leading-relaxed">
                      {breach.description}
                    </p>

                    <div className="flex flex-wrap items-center gap-1.5 text-xs">
                      <span className="text-slate-400 font-medium">Exposed in this breach:</span>
                      {breach.dataClasses.map((dc, idx) => (
                        <span key={idx} className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[11px]">
                          {dc}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommended Actions */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-4">
            <h4 className="text-base font-bold text-white flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
              <span>Recommended Next Steps</span>
            </h4>
            <div className="space-y-2.5">
              {result.recommendedActions.map((action, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 rounded-xl bg-slate-950 border border-slate-800/80 text-xs text-slate-200">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{action}</span>
                </div>
              ))}
            </div>

            <div className="pt-4 flex flex-wrap items-center justify-between gap-4 border-t border-slate-800">
              <div className="text-xs text-slate-400">
                Want automatic alerts whenever your credentials appear in new breaches?
              </div>
              <Link
                to="/pricing"
                className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs transition-colors"
              >
                Start Continuous Monitoring ($9.95/mo)
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
